import React, { useState, useEffect, useContext, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Bell, CirclePower, User, PersonStanding, X } from "lucide-react"; 
import logo from "./logo.png";
import { AuthContext } from "../context/AuthContext";
import AccessibilityPanel from "./AccessibilityPanel";
// =======================================================================
// 🔔 FIREBASE FOREGROUND LISTENERS IMPORT
// =======================================================================
import { messaging } from "../firebaseClient";
import { onMessage } from "firebase/messaging";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showNotifPopup, setShowNotifPopup] = useState(false);
  const { logout, language, userRole, updateLanguage, user, accessToken } = useContext(AuthContext);
  const [a11yOpen, setA11yOpen] = useState(false);
  const safeUser = user || {};
  const a11yButtonRef = useRef(null);
  const a11yRef = useRef(null);
  const navigate = useNavigate();
  const hamburgerRef = useRef(null);
  const firstMenuLinkRef = useRef(null);
  const prevMenuOpenRef = useRef(false);

  const notifContainerRef = useRef(null);

  // =======================================================================
  // 🔔 LOGISTICS STATE FOR PERSISTED LEDGER
  // =======================================================================
  const [notificationsList, setNotificationsList] = useState([]);
  const [hasNotifications, setHasNotifications] = useState(false);
  const [activeNotification, setActiveNotification] = useState(null);

  const translations = {
    el: {
      favorites: "Τα αγαπημένα μου",
      myDiscounts: "Οι προσφορές μου",
      viewDiscounts: "Δες προσφορές",
      addNew: "Νέα προσφορά",
      noNotifications: "Δεν υπάρχουν νέες ειδοποιήσεις",
      notifications: "Ειδοποιήσεις",
      logout: "Αποσύνδεση",
      profile: "Προφίλ",
      ok: "Εντάξει",
      viewAll: "Διαχείριση προσφορών",
      manageStores: "Διαχείριση καταστημάτων",
      manageCategories: "Διαχείριση κατηγοριών",
      manageUsers: "Διαχείριση χρηστών",
      viewStatistics: "Στατιστικά",
      newOfferAlert: "Νέα Προσφορά!",
    },
    en: {
      favorites: "My favourites list",
      myDiscounts: "My offers list",
      viewDiscounts: "View offers",
      addNew: "Add new offer",
      noNotifications: "No new notifications",
      notifications: "Notifications",
      logout: "Logout",
      profile: "Profile",
      ok: "OK",
      viewAll: "Manage offers",
      manageStores: "Manage stores",
      manageCategories: "Manage categories",
      manageUsers: "Manage users",
      viewStatistics: "Analytics",
      newOfferAlert: "New Offer Available!",
    }
  };

  const t = translations[language] || translations.el;

  const fetchUserNotifications = async () => {
    if (!safeUser.user_id || userRole === 'admin' || !accessToken) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/notifications/ledger/${safeUser.user_id}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        setNotificationsList(data.notifications || []);

        const hasUnread = data.notifications.some(n => !n.is_read);
        setHasNotifications(hasUnread);
      }
    } catch (err) {
      console.error("Failed to load historical notifications ledger:", err);
    }
  };

  useEffect(() => {
    if (user?.user_id && accessToken) {
      fetchUserNotifications();
    }
  }, [user, accessToken]);

  useEffect(() => {
    if (!messaging) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      setHasNotifications(true);
      setActiveNotification({
        title: payload.notification?.title || t.newOfferAlert,
        body: payload.notification?.body || "",
        discountId: payload.data?.discount_id || null
      });

      if (user?.user_id && accessToken) {
        fetchUserNotifications();
      }
    });

    return () => unsubscribe();
  }, [user, accessToken, t.newOfferAlert]);

  useEffect(() => {
    if (menuOpen && firstMenuLinkRef.current) {
      firstMenuLinkRef.current.focus();
    } else if (!menuOpen && prevMenuOpenRef.current && hamburgerRef.current) {
      hamburgerRef.current.focus();
    }
    prevMenuOpenRef.current = menuOpen;
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  useEffect(() => {
    const handleClick = (e) => {
      if (a11yRef.current && !a11yRef.current.contains(e.target) && !a11yButtonRef.current?.contains(e.target)) {
        setA11yOpen(false);
      }
      if (notifContainerRef.current && !notifContainerRef.current.contains(e.target)) {
        setShowNotifPopup(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setA11yOpen(false);
        setShowNotifPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleBellClick = async () => {
    setShowNotifPopup(prev => !prev);
  };

  const handleNotificationClick = (e) => {
    if (e.target.closest('.close-toast-btn')) return;

    if (activeNotification?.discountId) {
      navigate("/viewdiscount", {
        state: {
          discountId: Number(activeNotification.discountId),
        },
      });
    } else {
      navigate("/discounts");
    }

    setActiveNotification(null);
  };

  const linkClass = (isActive) =>
    `font-bold text-xs md:text-sm lg:text-base px-2 py-1.5 rounded-md transition whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-custom-letters ${
      isActive ? "bg-custom-background text-custom-grad2" : "text-custom-grad2 hover:underline"
    }`;

  const customerNavLinks = (isMobile = false) => (
    <>
      <NavLink ref={isMobile ? firstMenuLinkRef : null} to="/favorites" className={({ isActive }) => linkClass(isActive)}>
        {t.favorites}
      </NavLink>
      <NavLink to="/mydiscounts" className={({ isActive }) => linkClass(isActive)}>
        {t.myDiscounts}
      </NavLink>
      <NavLink to="/discounts" className={({ isActive }) => linkClass(isActive)}>
        {t.viewDiscounts}
      </NavLink>
      <NavLink to="/adddiscount" className={({ isActive }) => linkClass(isActive)}>
        {t.addNew}
      </NavLink>
    </>
  );

  const adminNavLinks = (isMobile = false) => (
    <>
      <NavLink ref={isMobile ? firstMenuLinkRef : null} to="/admindiscounts" className={({ isActive }) => linkClass(isActive)}>
        {t.viewAll}
      </NavLink>
      <NavLink to="/adminstores" className={({ isActive }) => linkClass(isActive)}>
        {t.manageStores}
      </NavLink>
      <NavLink to="/admincategories" className={({ isActive }) => linkClass(isActive)}>
        {t.manageCategories}
      </NavLink>
      <NavLink to="/adminusers" className={({ isActive }) => linkClass(isActive)}>
        {t.manageUsers}
      </NavLink>
      <NavLink to="/admindashboard" className={({ isActive }) => linkClass(isActive)}>
        {t.viewStatistics}
      </NavLink>
    </>
  );

  const navLinks = userRole === 'admin' ? adminNavLinks : customerNavLinks;

  return (
    <>
      <nav
        aria-label={language === 'el' ? 'Κύρια πλοήγηση' : 'Main navigation'}
        className="w-full bg-custom-header px-4 md:px-6 py-3 shadow-md sticky top-0 z-50 flex items-center justify-between">

        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-custom-background focus:text-custom-letters focus:px-4 focus:py-2 focus:rounded focus:font-bold">
          {language === 'el' ? 'Μετάβαση στο κύριο περιεχόμενο' : 'Skip to main content'}
        </a>

        <div className="flex items-center space-x-4 relative">
          <NavLink
            to={userRole === 'admin' ? '/adminmenu' : '/customermenu'}
            aria-label={language === 'el' ? 'Αρχική σελίδα' : 'Go to homepage'}
            className="flex items-center space-x-2 rounded focus:outline-none focus:ring-2 focus:ring-custom-letters">
            <img src={logo} alt="" className="h-10 w-auto md:h-12 cursor-pointer" />
            <span className="hidden sm:inline font-bold text-custom-letters text-xl">ShareTheDeal</span>
          </NavLink>

          <button
            ref={hamburgerRef}
            className="xl:hidden text-custom-letters text-3xl font-bold focus:outline-none focus:ring-2 focus:ring-custom-letters rounded"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? (language === 'el' ? 'Κλείσιμο μενού' : 'Close menu') : (language === 'el' ? 'Άνοιγμα μενού' : 'Open menu')}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu-b">
            ☰
          </button>

          <div className="hidden xl:flex items-center space-x-2" onClick={() => setMenuOpen(false)}>
            {navLinks(false)}
          </div>

          {menuOpen && (
            <div id="mobile-menu-b" className="absolute top-full left-0 mt-2 w-64 min-w-max bg-custom-header rounded-lg shadow-lg xl:hidden z-[100] border border-custom-grad2/20">
              <div className="flex flex-col p-3 space-y-1" onClick={() => setMenuOpen(false)}>
                {navLinks(true)}
              </div>
            </div>
          )}
        </div>

        {/* Action icons row */}
        <div className="flex items-center gap-4">
          
          {/* Stack 1: Accessibility & Notification */}
          <div className="flex flex-col xl:flex-row items-center gap-2 xl:gap-4">
            
            {/* Accessibility */}
            <div ref={a11yRef} className="relative xl:hidden flex items-center">
              <button
                ref={a11yButtonRef}
                onClick={() => setA11yOpen(v => !v)}
                className="h-8 w-8 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-custom-letters transition rounded-full">
                <PersonStanding className={`h-full w-full rounded-full border-2 border-custom-letters ${a11yOpen ? 'bg-custom-grad1 text-white border-custom-grad1' : 'bg-custom-background'}`} />
              </button>
              {a11yOpen && (
                <AccessibilityPanel
                  language={language}
                  className="absolute top-full right-[-100px] sm:right-0 mt-2 max-w-[calc(100vw-2rem)] z-[9999]"
                />
              )}
            </div>

            {/* Bell Notification */}
            {userRole !== 'admin' && (
              <div ref={notifContainerRef} className="relative flex items-center">
                <button
                  onClick={handleBellClick}
                  aria-label={`${t.notifications}${hasNotifications ? ' - new alerts' : ''}`}
                  className="relative text-custom-letters hover:text-purple-300 transition focus:outline-none focus:ring-2 focus:ring-custom-letters rounded-full">
                  <Bell className="bg-custom-background rounded-full border-2 border-custom-letters h-8 w-8" />
                  {hasNotifications && (
                    <span className="absolute top-0 right-0 h-3 w-3 bg-custom-grad1 rounded-full border-2 border-white a11y-notif-dot"></span>
                  )}
                </button>

                {/* HISTORICAL NOTIFICATIONS POP-UP DROPDOWN */}
                {showNotifPopup && (
                  <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="header-notif-title"
                    className="fixed sm:absolute top-[72px] sm:top-full right-2 sm:right-0 mt-3 bg-custom-header border border-custom-grad2/20 rounded-3xl p-4 sm:p-5 w-[calc(100vw-1rem)] sm:w-96 max-w-[400px] shadow-2xl flex flex-col max-h-[60vh] z-[9999]"
                  >
                    <h3 id="header-notif-title" className="text-lg font-bold text-custom-grad2 border-b-2 border-custom-grad2/10 pb-2 text-center mb-3">
                      {t.notifications}
                    </h3>

                    <div className="flex-1 overflow-y-auto scrollbar-thin overflow-x-hidden space-y-3 pr-2.5 my-1 text-left">
                      {notificationsList.length === 0 ? (
                        <p className="text-sm text-custom-letters/60 text-center py-6">{t.noNotifications}</p>
                      ) : (
                        notificationsList.map((notif) => (
                          <button
                            type="button"
                            key={notif.notification_id}
                            onClick={async () => {
                              setShowNotifPopup(false);

                              if (!notif.is_read) {
                                try {
                                  await fetch(`${process.env.REACT_APP_BACKEND_URL}/notifications/mark-read/${notif.notification_id}`, {
                                    method: "POST",
                                    headers: {
                                      "Content-Type": "application/json",
                                      Authorization: `Bearer ${accessToken}`
                                    }
                                  });
                                  fetchUserNotifications();
                                } catch (err) {
                                  console.error("Failed to mark notification as read dynamically:", err);
                                }
                              }

                              if (notif.discount_id) {
                                navigate("/viewdiscount", {
                                  state: {
                                    discountId: Number(notif.discount_id),
                                  },
                                });
                              } else {
                                navigate("/discounts");
                              }
                            }}
                            className={`w-full p-3 rounded-2xl text-left border transition cursor-pointer hover:bg-custom-background flex flex-col gap-1 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-black ${!notif.is_read
                              ? "a11y-notif-card bg-gradient-to-r from-custom-grad1/5 to-custom-grad2/5 border-2 border-custom-grad2 font-bold shadow-md"
                              : "bg-transparent a11y-notif-read border-custom-letters/10 text-custom-letters/80 font-normal"
                              }`}
                          >
                            <p className="text-custom-letters text-xs sm:text-sm line-clamp-3">
                              {language === 'en' ? notif.message_en : notif.message_el}
                            </p>
                            <span className="text-[10px] text-custom-letters/40 self-end mt-1">
                              {new Date(notif.created_at).toLocaleTimeString(language === 'el' ? 'el-GR' : 'en-US', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </button>
                        ))
                      )}
                    </div>

                    <button
                      onClick={() => setShowNotifPopup(false)}
                      className="w-full mt-3 px-5 py-2 rounded-full bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-white text-sm font-bold transform transition active:scale-95 shadow-md">
                      {t.ok}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Stack 2: Language Switcher & Profile Menu */}
          <div className="flex flex-col xl:flex-row items-center gap-2 xl:gap-4">
            
            {/* Language Switcher - Exact match with ProfileSidebar desktop button styling */}
            <button
              onClick={() => updateLanguage(language === 'el' ? 'en' : 'el')}
              aria-label={language === 'el' ? 'Αλλαγή γλώσσας, τρέχουσα: Ελληνικά' : 'Change language, current: English'}
              className="xl:hidden hover:scale-110 transition focus:outline-none focus:ring-2 focus:ring-custom-letters rounded-full">
              <div className="w-8 h-8 flex items-center justify-center bg-custom-background rounded-full border-2 border-custom-letters font-bold text-xs text-custom-letters">
                {language.toUpperCase()}
              </div>
            </button>

            {/* Profile Icon */}
            {userRole !== 'admin' && (
              <button
                onClick={() => navigate("/profilemenu")}
                aria-label={t.profile}
                className="flex items-center justify-center text-custom-letters hover:text-purple-300 transition focus:outline-none focus:ring-2 focus:ring-custom-letters rounded-full">
                <User className="bg-custom-background rounded-full border-2 border-custom-letters h-8 w-8" />
              </button>
            )}
          </div>

          {/* Logout Icon */}
          <button
            onClick={handleLogout}
            aria-label={t.logout}
            className="relative text-custom-letters hover:text-purple-300 transition focus:outline-none focus:ring-2 focus:ring-custom-letters rounded-full">
            <CirclePower size={35} className="bg-custom-background rounded-full border-2 border-custom-letters" />
          </button>
        </div>
      </nav>

      {/* FOREGROUND TOAST NOTIFICATION */}
      {activeNotification && (
        <div
          onClick={handleNotificationClick}
          role="alert"
          className="fixed top-24 right-4 left-4 sm:left-auto max-w-sm bg-custom-header border-2 border-custom-grad2 rounded-2xl p-4 shadow-2xl z-[99999] cursor-pointer transform transition-all duration-300 hover:scale-[1.02] flex items-start gap-3 animate-bounce"
        >
          <div className="p-2 bg-gradient-to-br from-custom-grad1 to-custom-grad2 rounded-full text-white mt-1 shrink-0">
            <Bell size={18} />
          </div>
          <div className="flex-1 min-w-0 text-left overflow-hidden break-words">
            <h4 className="font-bold text-sm text-custom-grad2 truncate">{activeNotification.title}</h4>
            <p className="text-xs text-custom-letters mt-1 line-clamp-3 whitespace-normal break-all">
              {activeNotification.body}
            </p>
          </div>
          <button
            onClick={() => setActiveNotification(null)}
            className="close-toast-btn p-1 text-custom-letters/60 hover:text-custom-letters focus:outline-none rounded-full hover:bg-custom-background transition shrink-0"
            aria-label="Dismiss banner"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </>
  );
};

export default Header;